import { ElementRef, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { filter, map, Observable, Subject } from 'rxjs';
import { IFrameMessage } from '../types/player-types';
import { ConsumerJsonModel, PlayerSetting } from '../../consumer/consumer.json.model';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../app.state';

@Injectable({
  providedIn: 'root'
})
export class PlayerMessageService implements OnDestroy {
  private readonly store = inject(Store);
  private _iFrameReady = signal(false); // true if player in iFrame ist loaded
  private _playerPreviewIFrame: ElementRef<HTMLIFrameElement> | null = null;
  private messageSource = new Subject<IFrameMessage>();
  private _pageIndex = -1;
  private _index = -1;
  private appState$ = this.store.select(AppSelectors.appState);

  public messages$: Observable<IFrameMessage> = this.messageSource.asObservable();

  constructor() {
    window.addEventListener('message', this.handleIFrameMessage.bind(this));
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleIFrameMessage.bind(this));
    this._pageIndex = -1;
    this._index = -1;
    this._iFrameReady.set(false);
  }

  private handleIFrameMessage(event: MessageEvent): void {
    const expectedOrigin = environment.playerBaseUrl;

    if (event.origin !== expectedOrigin.split('?')[0] && event.origin !== expectedOrigin) {
      return;
    }

    if (!environment.isProd) {
      // console.log(`::==> Get message from IFrame: ${JSON.stringify(event.data)} <==::`);
    }

    const data = event.data as IFrameMessage;
    this.messageSource.next(data);
  }

  public set iFrameReady(value: boolean) {
    this._iFrameReady.set(value);
    if (!value) {
      this._playerPreviewIFrame = null;
    }
  }

  public get iFrameReady() {
    return this._iFrameReady();
  }

  public set playerPreviewIFrame(value: ElementRef<HTMLIFrameElement> | null) {
    this._playerPreviewIFrame = value;
  }

  public get playerPreviewIFrame() {
    return this._playerPreviewIFrame;
  }

  public set pageIndex(value: number) {
    this._pageIndex = value;
  }

  public get pageIndex() {
    return this._pageIndex;
  }

  public set index(value: number) {
    this._index = value;
  }

  public get index() {
    return this._index;
  }

  public getPlayerSettings$(consumerId: string): Observable<PlayerSetting | null> {
    return this.appState$.pipe(
      filter(state => state.isLoaded),
      map(state => {
        const currentConsumer = state.consumers.find(
          consumer => consumer.id === consumerId
        );
        return currentConsumer?.playerSettings || null;
      })
    );
  }

  public getConsumer$(consumerId: string): Observable<ConsumerJsonModel | null> {
    return this.appState$.pipe(
      filter(state => state.isLoaded),
      map(state => {
        const currentConsumer = state.consumers.find(
          consumer => consumer.id === consumerId
        );
        return currentConsumer || null;
      })
    );
  }

  public openPlayerPreview(consumerId: string, kind: 'exercise' | 'exam' | 'sequence' | 'sequenceGroup' | 'exam_sequence' | 'explanation', id: string, usePreview = true) {
    if (!environment.playerBaseUrl) {
      return;
    }

    const baseUrl = environment.playerBaseUrl;

    let previewId = '';
    let previewType = '';
    switch (kind) {
      case 'exercise':
        previewId = 'exerciseId';
        previewType = 'exercise';
        break;
      case 'exam':
        previewId = 'examId';
        previewType = 'exam';
        break;
      case 'sequence':
        previewId = 'sequenceId';
        previewType = 'sequence';
        break;
      case 'sequenceGroup':
        previewId = 'sequenceId';
        previewType = 'sequenceGroup';
        break;
      case 'exam_sequence':
        previewId = 'sequenceId';
        previewType = 'examSequence';
        break;
    }

    let location: string;
    if (kind === 'explanation') {
      const previewParam = usePreview ? '&preview=true' : '';
      location = `${baseUrl}/player/v1/ui?consumerId=${consumerId}&explanationId=${id}&type=explanation&reda=true&backendUrl=${environment.backendUrl}${previewParam}`;
    }
    else {
      location = `${baseUrl}/player/v1/ui?preview=true&consumerId=${consumerId}&${previewId}=${id}&type=${previewType}&reda=true&backendUrl=${environment.backendUrl}`;
    }

    window.open(location, '_blank', 'noopener,noreferrer');
  }

  public theme2PlayerPreviewUrl(consumerId: string): string {
    return `${environment.playerBaseUrl}/player/v1/ui?preview=true&themePreview=true&consumerId=${consumerId}`;
  }

  public iFramePlayerPreviewUrl(consumerId: string): string {
    return `${environment.playerBaseUrl}?consumerId=${consumerId}&preview=true&iframePreview=true`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public postMessageToPlayer(url: string, message: any): void {
    if (this._playerPreviewIFrame && this.iFrameReady) {
      if (this._playerPreviewIFrame.nativeElement.contentWindow) {
        const targetOrigin = new URL(url).origin;
        this._playerPreviewIFrame.nativeElement.contentWindow.postMessage(message, targetOrigin);

        if (!environment.isProd) {
          console.log('::==> Send message to Player:', JSON.stringify(message) + ' <==::');
        }
      }
    }
  }
}
